/**
 * LegalList.jsx — Legal document management
 *
 * Features:
 * - Table display: Type, Party, Status badge, Date, File
 * - Status filter: All, Pending, Active, Expired
 * - Search by party name
 * - Create/Edit modal with:
 *   - Smart contact search (all contacts) + add-new-contact inline form
 *   - Multipart file upload via /api/upload/legal-doc
 *   - Type: contract, nda, ica, agreement, license, other
 * - Delete with confirmation
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { fmtD, cn, uid } from '../../lib/utils';
import { useLegalDocs, useCreateLegalDoc, useUpdateLegalDoc, useDeleteLegalDoc } from '../../hooks/useLegal';
import { useContacts, useCreateContact } from '../../hooks/useContacts';
import { useJobs } from '../../hooks/useJobs';
import { useAddToRoster } from '../../hooks/useRoster';
import { useToast } from '../layout/Toast';
import { useMobile } from '../../hooks/useMobile';

const TYPE_LABELS = { contract: 'Contract', nda: 'NDA', ica: 'ICA', agreement: 'Agreement', license: 'License', other: 'Other' };
const CONTACT_TYPE_COLORS = { client: 'text-accent', crew: 'text-green', vendor: 'text-yellow-400' };

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function LegalList() {
  const { data: docs = [], isLoading, error } = useLegalDocs();
  const { data: allContacts = [] } = useContacts();
  const { data: allJobs = [] } = useJobs();
  const createMutation = useCreateLegalDoc();
  const updateMutation = useUpdateLegalDoc();
  const deleteMutation = useDeleteLegalDoc();
  const createContactMutation = useCreateContact();
  const addToRosterMutation = useAddToRoster();
  const { toast } = useToast();
  const { isMobile } = useMobile();

  // ── List filters ─────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── Modal state ──────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({ type: 'contract', status: 'pending', date: '', notes: '', jobId: '' });
  const [selectedContact, setSelectedContact] = useState(null);      // { id, name, type, company }
  const [contactQuery, setContactQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);               // File object
  const [existingFile, setExistingFile] = useState(null);             // { name, path } from edit
  const [isUploading, setIsUploading] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // ── Add-new-contact inline form ──────────────────────────────────────────
  const [showAddNew, setShowAddNew] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', type: 'client', company: '', role: '', email: '', phone: '' });

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Filtered contact results ─────────────────────────────────────────────
  const contactResults = useMemo(() => {
    if (!contactQuery.trim()) return allContacts.slice(0, 20);
    const q = contactQuery.toLowerCase();
    return allContacts.filter(
      (c) => (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [allContacts, contactQuery]);

  // ── Filtered doc list ────────────────────────────────────────────────────
  const filteredDocs = useMemo(() => {
    let result = docs;
    if (statusFilter !== 'All') result = result.filter((d) => d.status === statusFilter.toLowerCase());
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((d) => (d.party || '').toLowerCase().includes(q));
    }
    return result;
  }, [docs, search, statusFilter]);

  // ── Form helpers ─────────────────────────────────────────────────────────
  const resetModal = () => {
    setFormData({ type: 'contract', status: 'pending', date: '', notes: '', jobId: '' });
    setSelectedContact(null);
    setContactQuery('');
    setShowDropdown(false);
    setPendingFile(null);
    setExistingFile(null);
    setShowAddNew(false);
    setNewContact({ name: '', type: 'client', company: '', role: '', email: '', phone: '' });
  };

  const handleNewDoc = () => {
    setEditingDoc(null);
    resetModal();
    setShowForm(true);
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    // Normalize date — PostgreSQL DATE arrives as ISO string "2026-04-09T07:00:00.000Z"
    // but <input type="date"> requires YYYY-MM-DD
    const rawDate = doc.date ? String(doc.date).slice(0, 10) : '';
    setFormData({
      type: doc.type || 'contract',
      status: doc.status || 'pending',
      date: rawDate,
      notes: doc.notes || '',
      jobId: doc.jobId || doc.job_id || '',
    });
    // Resolve linked contact
    const linked = doc.contactId ? allContacts.find((c) => c.id === doc.contactId) : null;
    setSelectedContact(linked ? { id: linked.id, name: linked.name, type: linked.type, company: linked.company } : null);
    setContactQuery('');
    setShowDropdown(false);
    setPendingFile(null);
    setExistingFile(doc.filePath ? { name: doc.fileName, path: doc.filePath } : null);
    setShowAddNew(false);
    setNewContact({ name: '', type: 'client', company: '', role: '', email: '', phone: '' });
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectContact = (contact) => {
    setSelectedContact({ id: contact.id, name: contact.name, type: contact.type, company: contact.company });
    setContactQuery('');
    setShowDropdown(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast('File must be under 10 MB', 'error'); return; }
    setPendingFile(file);
  };

  // ── Add New Contact ──────────────────────────────────────────────────────
  const handleAddNewContact = async () => {
    const n = newContact.name.trim();
    if (!n) { toast('Contact name is required', 'error'); return; }
    try {
      const created = await createContactMutation.mutateAsync({
        id: uid(),
        name: n,
        type: newContact.type,
        company: newContact.company || null,
        email: newContact.email || null,
        phone: newContact.phone || null,
      });
      // If crew, also add to roster
      if (newContact.type === 'crew') {
        await addToRosterMutation.mutateAsync({
          name: n,
          role: newContact.role || null,
          email: newContact.email || null,
          phone: newContact.phone || null,
        });
      }
      setSelectedContact({ id: created.id, name: created.name, type: created.type, company: created.company });
      setShowAddNew(false);
      setNewContact({ name: '', type: 'client', company: '', role: '', email: '', phone: '' });
      toast('Contact created');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Upload file ──────────────────────────────────────────────────────────
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/upload/legal-doc', { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) throw new Error('File upload failed');
    return res.json(); // { url, fileName }
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedContact) { toast('Please select or create a contact', 'error'); return; }
    try {
      setIsUploading(true);
      let filePath = existingFile?.path || null;
      let fileName = existingFile?.name || null;

      if (pendingFile) {
        const uploaded = await uploadFile(pendingFile);
        filePath = uploaded.url;
        fileName = uploaded.fileName;
      }

      const record = {
        type: formData.type,
        status: formData.status,
        date: formData.date || null,
        notes: formData.notes || null,
        party: selectedContact.name,
        contactId: selectedContact.id,
        jobId: formData.jobId || null,
        filePath,
        fileName,
      };

      if (editingDoc) {
        await updateMutation.mutateAsync({ id: editingDoc.id, ...record });
        toast('Document updated');
      } else {
        await createMutation.mutateAsync({ id: uid(), ...record });
        toast('Document created');
      }
      setShowForm(false);
      setEditingDoc(null);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteMutation.mutateAsync(docId);
      toast('Document deleted');
      setDeleteConfirm(null);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const statusColorMap = {
    pending: 'bg-yellow-600/20 border-yellow-600/30 text-yellow-400',
    active: 'bg-green/20 border-green/30 text-green',
    expired: 'text-text-dim border-border bg-surface',
  };

  const inputCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors';
  const labelCls = 'block text-sm font-medium text-text-primary mb-1';
  const isSaving = isUploading || createMutation.isPending || updateMutation.isPending;

  if (error) {
    return <div className="card p-4 text-red-accent text-sm">Failed to load legal documents: {error.message}</div>;
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading text-text-primary">Legal Documents</h2>
        <button onClick={handleNewDoc} className="px-3 py-1.5 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors">
          + New Document
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search by party..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors"
        />
        <div className="flex gap-2 flex-wrap">
          {['All', 'Pending', 'Active', 'Expired'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn('px-3 py-2 rounded text-xs font-medium transition-colors', statusFilter === s ? 'bg-accent text-bg' : 'bg-surface border border-border text-text-dim hover:border-accent')}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Documents Table */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="text-text-dim text-sm p-4">Loading documents...</div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-text-dim text-sm p-4">No documents found</div>
        ) : (
          <div className="overflow-x-auto">
          {isMobile ? (
            /* ── Mobile: Card layout ── */
            <div className="space-y-2">
              {filteredDocs.map((doc) => {
                const linked = doc.contactId ? allContacts.find((c) => c.id === doc.contactId) : null;
                const jid = doc.jobId || doc.job_id;
                const job = jid ? allJobs.find(j => j.id === jid) : null;
                return (
                  <div key={doc.id} className="card p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-text-primary">{TYPE_LABELS[doc.type] || doc.type || '—'}</span>
                        <span className={cn('px-2 py-0.5 rounded text-2xs font-medium border', statusColorMap[doc.status] || statusColorMap.pending)}>
                          {doc.status}
                        </span>
                      </div>
                      <div className="flex gap-3 -my-1">
                        <button onClick={() => handleEditDoc(doc)} className="text-accent text-xs font-medium px-2 py-2 min-h-[40px] inline-flex items-center">Edit</button>
                        <button onClick={() => setDeleteConfirm(doc.id)} className="text-red-accent text-xs font-medium px-2 py-2 min-h-[40px] inline-flex items-center">Delete</button>
                      </div>
                    </div>
                    <div className="text-sm text-text-primary">
                      {doc.party || '—'}
                      {linked && <span className={cn('ml-1 text-2xs', CONTACT_TYPE_COLORS[linked.type] || 'text-text-dim')}>{linked.type}</span>}
                    </div>
                    {job && <div className="text-2xs text-text-muted truncate">Job: {job.title || job.clientName || jid}</div>}
                    <div className="flex items-center gap-3 text-xs text-text-dim">
                      {doc.date && <span>{fmtD(doc.date)}</span>}
                      {doc.filePath && (
                        <a href={doc.filePath} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-xs py-1 inline-block">
                          📎 {doc.fileName || 'View'}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── Desktop: Table layout ── */
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-2 text-left font-heading text-text-primary">Type</th>
                <th className="px-4 py-2 text-left font-heading text-text-primary">Party</th>
                <th className="px-4 py-2 text-left font-heading text-text-primary">Job</th>
                <th className="px-4 py-2 text-left font-heading text-text-primary">Status</th>
                <th className="px-4 py-2 text-left font-heading text-text-primary">Date</th>
                <th className="px-4 py-2 text-left font-heading text-text-primary">File</th>
                <th className="px-4 py-2 text-right font-heading text-text-primary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((doc) => {
                const linked = doc.contactId ? allContacts.find((c) => c.id === doc.contactId) : null;
                return (
                  <tr key={doc.id} className="border-b border-border hover:bg-surface/50 transition-colors">
                    <td className="px-4 py-2 text-text-primary">{TYPE_LABELS[doc.type] || doc.type || '—'}</td>
                    <td className="px-4 py-2">
                      <span className="text-text-primary">{doc.party || '—'}</span>
                      {linked && (
                        <span className={cn('ml-2 text-xs', CONTACT_TYPE_COLORS[linked.type] || 'text-text-dim')}>
                          {linked.type}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-text-muted">
                      {(() => {
                        const jid = doc.jobId || doc.job_id;
                        if (!jid) return '—';
                        const job = allJobs.find(j => j.id === jid);
                        return job ? (job.title || job.clientName || jid) : jid;
                      })()}
                    </td>
                    <td className="px-4 py-2">
                      <span className={cn('px-2 py-1 rounded text-xs font-medium border', statusColorMap[doc.status] || statusColorMap.pending)}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-text-primary">{fmtD(doc.date)}</td>
                    <td className="px-4 py-2 text-text-dim">
                      {doc.filePath ? (
                        <a href={doc.filePath} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-xs">
                          📎 {doc.fileName || 'View'}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button onClick={() => handleEditDoc(doc)} className="text-accent hover:text-accent-hover text-xs font-medium">Edit</button>
                      <button onClick={() => setDeleteConfirm(doc.id)} className="text-red-accent hover:text-red-500 text-xs font-medium">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300]">
          <div className="card p-6 max-w-sm">
            <h3 className="font-heading text-text-primary mb-4">Delete Document?</h3>
            <p className="text-text-dim text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-3 py-2 bg-surface border border-border rounded text-sm hover:bg-surface-2 transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-3 py-2 bg-red-accent/20 border border-red-accent text-red-accent rounded text-sm hover:bg-red-accent/30 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Form Modal ──────────────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300]">
          <div className="card p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-text-primary mb-6">
              {editingDoc ? 'Edit Document' : 'New Document'}
            </h3>

            <div className="space-y-4">
              {/* Type */}
              <div>
                <label className={labelCls}>Type</label>
                <select name="type" value={formData.type} onChange={handleFormChange} className={inputCls}>
                  <option value="contract">Contract</option>
                  <option value="nda">NDA</option>
                  <option value="ica">ICA</option>
                  <option value="agreement">Agreement</option>
                  <option value="license">License</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* ── Party / Contact Search ─────────────────────────────── */}
              <div>
                <label className={labelCls}>
                  Party <span className="text-red-accent">*</span>
                </label>

                {selectedContact ? (
                  /* Selected contact chip */
                  <div className="flex items-center gap-2 px-3 py-2 bg-surface border border-accent/40 rounded">
                    <span className="text-text-primary text-sm font-medium">{selectedContact.name}</span>
                    <span className={cn('text-xs', CONTACT_TYPE_COLORS[selectedContact.type] || 'text-text-dim')}>
                      {selectedContact.type}
                    </span>
                    {selectedContact.company && (
                      <span className="text-text-dim text-xs">· {selectedContact.company}</span>
                    )}
                    <button
                      onClick={() => { setSelectedContact(null); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                      className="ml-auto text-text-dim hover:text-red-accent text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  /* Search input + dropdown */
                  <div className="relative" ref={dropdownRef}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={contactQuery}
                      onChange={(e) => { setContactQuery(e.target.value); setShowDropdown(true); setShowAddNew(false); }}
                      onFocus={() => setShowDropdown(true)}
                      placeholder="Search contacts..."
                      className={inputCls}
                    />
                    {showDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded shadow-lg max-h-48 overflow-y-auto">
                        {contactResults.length > 0 ? (
                          contactResults.map((c) => (
                            <button
                              key={c.id}
                              onClick={() => handleSelectContact(c)}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2"
                            >
                              <span className="text-text-primary">{c.name}</span>
                              <span className={cn('text-xs', CONTACT_TYPE_COLORS[c.type] || 'text-text-dim')}>
                                {c.type}
                              </span>
                              {c.company && <span className="text-text-dim text-xs">· {c.company}</span>}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-text-dim text-xs">No contacts found</div>
                        )}
                        <button
                          onClick={() => { setShowDropdown(false); setShowAddNew(true); setNewContact((p) => ({ ...p, name: contactQuery })); }}
                          className="w-full px-3 py-2 text-left text-sm text-accent hover:bg-accent/10 border-t border-border font-medium"
                        >
                          + Add New Contact
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Inline Add New Contact ──────────────────────────── */}
                {showAddNew && !selectedContact && (
                  <div className="mt-3 p-4 bg-surface border border-border rounded-lg space-y-3">
                    <h4 className="text-sm font-medium text-text-primary">Add New Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <label className="text-xs text-text-dim">Name *</label>
                        <input
                          type="text"
                          value={newContact.name}
                          onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))}
                          placeholder="Full name"
                          className={inputCls + ' mt-1'}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-text-dim">Type *</label>
                        <select
                          value={newContact.type}
                          onChange={(e) => setNewContact((p) => ({ ...p, type: e.target.value }))}
                          className={inputCls + ' mt-1'}
                        >
                          <option value="client">Client</option>
                          <option value="crew">Crew</option>
                          <option value="vendor">Vendor</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-text-dim">Company</label>
                        <input
                          type="text"
                          value={newContact.company}
                          onChange={(e) => setNewContact((p) => ({ ...p, company: e.target.value }))}
                          placeholder="Optional"
                          className={inputCls + ' mt-1'}
                        />
                      </div>
                      {newContact.type === 'crew' && (
                        <div className="col-span-2">
                          <label className="text-xs text-text-dim">Role</label>
                          <input
                            type="text"
                            value={newContact.role}
                            onChange={(e) => setNewContact((p) => ({ ...p, role: e.target.value }))}
                            placeholder="e.g. Pilot, Editor"
                            className={inputCls + ' mt-1'}
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-text-dim">Email</label>
                        <input
                          type="email"
                          value={newContact.email}
                          onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))}
                          className={inputCls + ' mt-1'}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-text-dim">Phone</label>
                        <input
                          type="tel"
                          value={newContact.phone}
                          onChange={(e) => setNewContact((p) => ({ ...p, phone: e.target.value }))}
                          className={inputCls + ' mt-1'}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => setShowAddNew(false)} className="px-3 py-1.5 text-xs text-text-dim hover:text-text-primary transition-colors">
                        Cancel
                      </button>
                      <button
                        onClick={handleAddNewContact}
                        disabled={createContactMutation.isPending}
                        className="px-3 py-1.5 bg-accent text-bg text-xs font-medium rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
                      >
                        {createContactMutation.isPending ? 'Adding...' : 'Add & Select'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label className={labelCls}>Status</label>
                <select name="status" value={formData.status} onChange={handleFormChange} className={inputCls}>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className={labelCls}>Date of Effectiveness</label>
                <input type="date" name="date" value={formData.date} onChange={handleFormChange} className={inputCls} />
              </div>

              {/* Notes */}
              <div>
                <label className={labelCls}>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  placeholder="Any additional notes..."
                  rows={3}
                  className={inputCls}
                />
              </div>

              {/* Link to Job */}
              <div>
                <label className={labelCls}>Link to Job (optional)</label>
                <select name="jobId" value={formData.jobId} onChange={handleFormChange} className={inputCls}>
                  <option value="">— No job —</option>
                  {allJobs.filter(j => j.status !== 'archived').map(j => (
                    <option key={j.id} value={j.id}>{j.title || j.clientName || j.id}</option>
                  ))}
                </select>
              </div>

              {/* ── File Upload ────────────────────────────────────────── */}
              <div>
                <label className={labelCls}>File Attachment</label>
                {existingFile && !pendingFile && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-surface border border-border rounded text-sm">
                    <span className="text-text-dim">📎</span>
                    <a href={existingFile.path} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-xs">
                      {existingFile.name}
                    </a>
                    <button onClick={() => setExistingFile(null)} className="ml-auto text-text-dim hover:text-red-accent text-xs">Remove</button>
                  </div>
                )}
                <label className="flex flex-col items-center justify-center gap-1 px-4 py-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-accent/50 transition-colors">
                  <span className="text-text-dim text-sm">{pendingFile ? '✓ File selected' : 'Click to browse'}</span>
                  <span className="text-text-dim text-xs">PDF, DOC, DOCX, JPG, PNG — max 10 MB</span>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                    className="hidden"
                  />
                </label>
                {pendingFile && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-text-dim">
                    <span>📎 {pendingFile.name} ({fmtSize(pendingFile.size)})</span>
                    <button onClick={() => setPendingFile(null)} className="text-red-accent hover:text-red-500">Remove</button>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-surface border border-border rounded text-sm hover:bg-surface-2 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !selectedContact}
                className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
